import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EmployeePermissionsPage = () => {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeRoles, setEmployeeRoles] = useState([]);
  const [employeePermissions, setEmployeePermissions] = useState(null);
  const [customPermissions, setCustomPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const [employeesRes, rolesRes] = await Promise.all([
        axios.get(`${API_URL}/api/hr/employees`, config),
        axios.get(`${API_URL}/api/rbac/roles`, config)
      ]);

      setEmployees(employeesRes.data || []);
      setRoles(rolesRes.data || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchEmployeeData = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const [rolesRes, permsRes, customRes] = await Promise.all([
        axios.get(`${API_URL}/api/rbac/employees/${employeeId}/roles`, config),
        axios.get(`${API_URL}/api/rbac/employees/${employeeId}/all-permissions`, config),
        axios.get(`${API_URL}/api/rbac/employees/${employeeId}/custom-permissions`, config)
      ]);

      setEmployeeRoles(rolesRes.data.roles || []);
      setEmployeePermissions(permsRes.data || null);
      setCustomPermissions(customRes.data || null);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setEmployeeRoles([]);
      setEmployeePermissions(null);
      setCustomPermissions(null);
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    fetchEmployeeData(employee.id);
  };

  const handleAddRole = async () => {
    if (!selectedEmployee || !selectedRoleToAdd) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      await axios.post(
        `${API_URL}/api/rbac/employees/${selectedEmployee.id}/roles`,
        {
          role_id: selectedRoleToAdd,
          is_primary: employeeRoles.length === 0 // First role is primary
        },
        config
      );

      alert('تم إضافة الدور بنجاح!');
      setShowAddRoleModal(false);
      setSelectedRoleToAdd('');
      fetchEmployeeData(selectedEmployee.id);
      setSaving(false);
    } catch (error) {
      console.error('Error adding role:', error);
      alert(error.response?.data?.detail || 'حدث خطأ أثناء إضافة الدور');
      setSaving(false);
    }
  };

  const handleRemoveRole = async (roleId) => {
    if (!selectedEmployee) return;
    
    if (!window.confirm('هل أنت متأكد من إزالة هذا الدور؟')) return;

    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      await axios.delete(
        `${API_URL}/api/rbac/employees/${selectedEmployee.id}/roles/${roleId}`,
        config
      );

      alert('تم إزالة الدور بنجاح!');
      fetchEmployeeData(selectedEmployee.id);
    } catch (error) {
      console.error('Error removing role:', error);
      alert('حدث خطأ أثناء إزالة الدور');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            إدارة صلاحيات الموظفين
          </h1>
          <p className="text-gray-600">
            ربط الموظفين بالأدوار وتخصيص الصلاحيات الفردية
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Employees List */}
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">قائمة الموظفين</h2>
            
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {employees.length === 0 ? (
                <p className="text-gray-500 text-center py-4">لا يوجد موظفين</p>
              ) : (
                employees.map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => handleEmployeeSelect(emp)}
                    className={`w-full text-right p-4 rounded-lg border-2 transition-all ${
                      selectedEmployee?.id === emp.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-white'
                    }`}
                  >
                    <div className="font-bold text-gray-900">{emp.full_name_ar}</div>
                    <div className="text-sm text-gray-500">{emp.position}</div>
                    <div className="text-xs text-gray-400 mt-1">{emp.department}</div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Employee Permissions Details */}
          <div className="col-span-12 md:col-span-8 bg-white rounded-lg shadow-sm p-6">
            {!selectedEmployee ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <p className="mt-4 text-gray-500">اختر موظفاً لعرض وإدارة صلاحياته</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Employee Info */}
                <div className="border-b border-gray-200 pb-4">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedEmployee.full_name_ar}</h2>
                  <p className="text-gray-600">{selectedEmployee.position} - {selectedEmployee.department}</p>
                </div>

                {/* Roles Section */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">الأدوار المُسندة</h3>
                    <button
                      onClick={() => setShowAddRoleModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      + إضافة دور
                    </button>
                  </div>

                  {employeeRoles.length === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <p className="text-yellow-800">لا يوجد أدوار مُسندة لهذا الموظف</p>
                      <p className="text-sm text-yellow-600 mt-1">الموظف لن يتمكن من الوصول لأي جزء من النظام</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3">
                      {employeeRoles.map((role) => (
                        <div key={role.id} className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div>
                            <p className="font-bold text-gray-900">{role.name_ar}</p>
                            <p className="text-sm text-gray-600">{role.name_en}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveRole(role.id)}
                            className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                          >
                            إزالة
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Permissions Summary */}
                {employeePermissions && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">ملخص الصلاحيات</h3>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-green-600 font-medium">إجمالي الصلاحيات</p>
                        <p className="text-2xl font-bold text-green-900">{employeePermissions.permissions.length}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-purple-600 font-medium">صلاحيات مخصصة</p>
                        <p className="text-2xl font-bold text-purple-900">
                          {employeePermissions.custom_grants.length + employeePermissions.custom_revokes.length}
                        </p>
                      </div>
                    </div>

                    {/* Permissions List */}
                    <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <p className="font-medium text-gray-700 mb-3">الصلاحيات الفعّالة:</p>
                      <div className="space-y-1">
                        {employeePermissions.permissions.map((perm, index) => (
                          <div key={index} className="flex items-center justify-between text-sm py-2 border-b border-gray-100">
                            <span className="text-gray-700">{perm.permission_name}</span>
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              perm.source === 'custom_grant' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {perm.source === 'custom_grant' ? 'مخصصة' : 'من الدور'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Permissions */}
                {customPermissions && (customPermissions.custom_grants.length > 0 || customPermissions.custom_revokes.length > 0) && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">الصلاحيات المخصصة</h3>
                    
                    {customPermissions.custom_grants.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-green-600 mb-2">صلاحيات ممنوحة:</p>
                        <div className="space-y-2">
                          {customPermissions.custom_grants.map((grant, index) => (
                            <div key={index} className="bg-green-50 border border-green-200 rounded p-3">
                              <p className="font-medium text-gray-900">{grant.permission.name_ar}</p>
                              {grant.reason && <p className="text-sm text-gray-600 mt-1">السبب: {grant.reason}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {customPermissions.custom_revokes.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-red-600 mb-2">صلاحيات ملغاة:</p>
                        <div className="space-y-2">
                          {customPermissions.custom_revokes.map((revoke, index) => (
                            <div key={index} className="bg-red-50 border border-red-200 rounded p-3">
                              <p className="font-medium text-gray-900">{revoke.permission.name_ar}</p>
                              {revoke.reason && <p className="text-sm text-gray-600 mt-1">السبب: {revoke.reason}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add Role Modal */}
        {showAddRoleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">إضافة دور للموظف</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اختر الدور
                </label>
                <select
                  value={selectedRoleToAdd}
                  onChange={(e) => setSelectedRoleToAdd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- اختر دوراً --</option>
                  {roles
                    .filter(role => !employeeRoles.some(er => er.id === role.id))
                    .map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name_ar} - {role.name_en}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAddRole}
                  disabled={!selectedRoleToAdd || saving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'جاري الإضافة...' : 'إضافة'}
                </button>
                <button
                  onClick={() => {
                    setShowAddRoleModal(false);
                    setSelectedRoleToAdd('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeePermissionsPage;
